import { useCallback, useMemo } from 'react';
import { useThemeCustom } from './useThemeCustom';

/**
 * Fallbacks padrão para labels do sistema
 * Usado quando o tema não está disponível ou não tem o label
 */
const DEFAULT_FALLBACKS = {
  // Entidades principais
  schedules: 'Horários',
  schedulesDescription: 'Gerencie seus horários',
  services: 'Serviços',
  servicesDescription: 'Gerencie seus serviços',
  professionals: 'Profissionais',
  professionalsDescription: 'Gerencie seus profissionais',
  places: 'Locais',
  placesDescription: 'Gerencie seus locais de atendimento',
  persons: 'Clientes',
  personsDescription: 'Gerencie seus clientes',
  absences: 'Ausências',
  absencesDescription: 'Gerencie bloqueios de agenda',
  appointments: 'Agendamentos',
  appointmentsDescription: 'Gerencie seus agendamentos',
  
  // Títulos
  appTitle: 'Agendário',
  dashboard: 'Dashboard',
  settings: 'Configurações',
  profile: 'Perfil',
  
  // Ações
  save: 'Salvar',
  cancel: 'Cancelar',
  delete: 'Excluir',
  edit: 'Editar',
  create: 'Criar',
  add: 'Adicionar',
  
  // Mensagens
  loading: 'Carregando...',
  error: 'Erro',
  success: 'Sucesso',
  noData: 'Nenhum dado encontrado',
};

/**
 * Hook que encapsula acesso seguro a labels e ícones do tema
 * 
 * Elimina a necessidade de try/catch em cada componente que usa getLabel/getIcon
 * Fornece fallbacks automáticos quando o tema não está disponível
 * 
 * @returns {Object} Objeto com funções seguras para acessar labels e ícones
 * 
 * @example
 * // Uso simples
 * const { getLabel, getIcon } = useThemeLabels();
 * 
 * // Com fallback automático
 * <Typography>{getLabel('services')}</Typography> // "Serviços" ou valor do tema
 * 
 * // Com fallback customizado
 * <Typography>{getLabel('customKey', 'Meu Fallback')}</Typography>
 */
export function useThemeLabels() {
  // Tenta obter o contexto do tema
  let themeContext = null;
  let contextError = null;
  
  try {
    themeContext = useThemeCustom();
  } catch (error) {
    contextError = error;
    console.debug('[useThemeLabels] Tema não disponível, usando fallbacks:', error.message);
  }

  /**
   * Obtém um label do tema com fallback automático
   * 
   * @param {string} key - Chave do label
   * @param {string} [customFallback] - Fallback customizado (opcional)
   * @returns {string} Label do tema ou fallback
   */
  const getLabel = useCallback((key, customFallback) => {
    // Se temos contexto, tenta obter do tema
    if (themeContext?.getLabel) {
      try {
        const value = themeContext.getLabel(key);
        if (value) return value;
      } catch (e) {
        console.debug(`[useThemeLabels] Erro ao obter label "${key}":`, e.message);
      }
    }
    
    // Retorna fallback: customizado > padrão > key
    return customFallback || DEFAULT_FALLBACKS[key] || key;
  }, [themeContext]);

  /**
   * Obtém configuração de ícone do tema com fallback
   * 
   * @param {string} key - Chave do ícone
   * @returns {Object} Configuração do ícone { name: string, color?: string }
   */
  const getIcon = useCallback((key) => {
    // Se temos contexto, tenta obter do tema
    if (themeContext?.getIcon) {
      try {
        const icon = themeContext.getIcon(key);
        if (icon) return icon;
      } catch (e) {
        console.debug(`[useThemeLabels] Erro ao obter ícone "${key}":`, e.message);
      }
    }
    
    // Fallbacks de ícones
    const iconFallbacks = {
      schedules: { name: 'Schedule' },
      services: { name: 'ContentCut' },
      professionals: { name: 'Person' },
      places: { name: 'Place' },
      persons: { name: 'People' },
      absences: { name: 'Block' },
      appointments: { name: 'EventAvailable' },
      dashboard: { name: 'Dashboard' },
      settings: { name: 'Settings' },
    };
    
    return iconFallbacks[key] || { name: 'HelpOutline' };
  }, [themeContext]);

  /**
   * Obtém uma cor do tema com fallback
   * 
   * @param {string} key - Chave da cor
   * @param {string} [fallback='#1976d2'] - Cor padrão se não encontrar
   * @returns {string} Cor em hex
   */
  const getColor = useCallback((key, fallback = '#1976d2') => {
    if (themeContext?.getColor) {
      try {
        const color = themeContext.getColor(key);
        if (color) return color;
      } catch (e) {
        console.debug(`[useThemeLabels] Erro ao obter cor "${key}":`, e.message);
      }
    }
    return fallback;
  }, [themeContext]);

  /**
   * Obtém uma mensagem do tema com fallback
   * 
   * @param {string} key - Chave da mensagem
   * @param {string} [customFallback] - Fallback customizado
   * @returns {string} Mensagem do tema ou fallback
   */
  const getMessage = useCallback((key, customFallback) => {
    if (themeContext?.getMessage) {
      try {
        const message = themeContext.getMessage(key);
        if (message) return message;
      } catch (e) {
        console.debug(`[useThemeLabels] Erro ao obter mensagem "${key}":`, e.message);
      }
    }
    return customFallback || key;
  }, [themeContext]);

  // Informações sobre o estado do tema (útil para debug)
  const themeInfo = useMemo(() => ({
    isAvailable: !!themeContext,
    themeId: themeContext?.themeId || null,
    loading: themeContext?.loading ?? false,
    error: contextError?.message || null,
  }), [themeContext, contextError]);

  return {
    getLabel,
    getIcon,
    getColor,
    getMessage,
    themeInfo,
    // Exposição do contexto original para casos avançados
    themeContext,
  };
}

export default useThemeLabels;
